import React, { useEffect, useState } from "react";
import { auth, db } from "../../firebase/config";
import { updatePassword, reauthenticateWithCredential, EmailAuthProvider } from "firebase/auth";
import { doc, updateDoc } from "firebase/firestore";
import { useCoachAuth } from "../../hooks/useCoachAuth";
import { completeOnboarding } from "../../services/coachAPI";

import DynamicAgreements from "./steps/DynamicAgreements";
import Step4BackgroundCheck from "./steps/Step4BackgroundCheck";
import Step5TrainingCourses from "./steps/Step5TrainingCourses";
import Step6Complete from "./steps/Step6Complete";

import {
  Eye,
  EyeOff,
  Lock,
  ShieldCheck,
  CheckCircle,
  Phone,
  FileText,
  GraduationCap,
  X,
} from "lucide-react";

export default function OnBoarding() {
  const { userData } = useCoachAuth();
  const coachId = userData?.id || userData?.uid;

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [completedSteps, setCompletedSteps] = useState({});
  const [currentStep, setCurrentStep] = useState(1);

  const hasSetPassword = userData?.passwordUpdated === true || (userData?.onboardStatus && userData?.onboardStatus !== 'invited');

  const [toast, setToast] = useState({
    show: false,
    type: "",
    title: "",
    message: "",
  });

  const onboardingSteps = [
    {
      id: 1,
      title: "Create Password",
      icon: Lock,
    },
    {
      id: 2,
      title: "Agreements",
      icon: FileText,
    },
    {
      id: 3,
      title: "Background Check",
      icon: Phone,
    },
    {
      id: 4,
      title: "Training Information",
      icon: GraduationCap,
    },
    {
      id: 5,
      title: "Final Review",
      icon: CheckCircle,
    },
  ];

  useEffect(() => {
    if (!userData) return;

    const storedCoachUser = JSON.parse(
      localStorage.getItem("coachUser") || "{}"
    );

    const onboardStatus =
      userData?.onboardStatus || storedCoachUser?.onboardStatus;

    const statusStepMap = {
      invited: 1,
      in_process: 2,
      phase_1_completed: 3,
      phase_2_completed: 3,
      phase_3_completed: 3,
      phase_4_completed: 4,
      phase_5_completed: 5,
      completed: 5,
    };

    const step = statusStepMap[onboardStatus] == "invited" ? 0 : '' || 1;

    // For testing it is given 4 
    setCurrentStep(4);

    const completed = {};

    for (let i = 1; i < step; i++) {
      completed[i] = true;
    }

    if (onboardStatus === "completed") {
      completed[step] = true;
    }

    setCompletedSteps(completed);
  }, [userData]);

  const showSuccessToast = () => {
    setToast({
      show: true,
      type: "success",
      title: "Password Updated Successfully",
      message: "Your password has been saved. You can continue onboarding now.",
    });

    setTimeout(() => {
      setToast({
        show: false,
        type: "",
        title: "",
        message: "",
      });
    }, 1800);
  };

  const handleStepComplete = async (stepId, stepData) => {
    setError("");

    try {
      setCompletedSteps((prev) => ({
        ...prev,
        [stepId]: true,
      }));

      if (stepId < onboardingSteps.length) {
        setCurrentStep(stepId + 1);
      }
    } catch (err) {
      console.error("Onboarding step error:", err);
      setError(
        err.message || "Failed to save onboarding step. Please try again."
      );
    }
  };

  const handleOnboardingComplete = async () => {
    setError("");

    try {
      if (coachId) {
        await completeOnboarding(coachId);
      }

      setCompletedSteps((prev) => ({
        ...prev,
        [onboardingSteps.length]: true,
      }));

      setCurrentStep(onboardingSteps.length);
    } catch (err) {
      console.error("Complete onboarding error:", err);
      setError(
        err.message || "Failed to complete onboarding. Please try again."
      );
    }
  };

  const handleNextStep = () => {
    setCompletedSteps((prev) => ({
      ...prev,
      [currentStep]: true,
    }));

    setCurrentStep((prev) => Math.min(prev + 1, onboardingSteps.length));
  };

  const handleBackStep = () => {
    setCurrentStep((prev) => Math.max(1, prev - 1));
  };

  const renderStepContent = () => {
    if (currentStep === 1) {
      return null;
    }

    const stepProps = {
      coachData: userData,
      onComplete: (data) => handleStepComplete(currentStep, data),
      onNext: handleNextStep,
      onBack: handleBackStep,
    };

    switch (currentStep) {
      case 2:
        return <DynamicAgreements {...stepProps} />;

      case 3:
        return <Step4BackgroundCheck {...stepProps} />;

      case 4:
        return <Step5TrainingCourses {...stepProps} />;

      case 5:
        return (
          <Step6Complete
            onComplete={handleOnboardingComplete}
            coachData={userData}
          />
        );

      default:
        return <DynamicAgreements {...stepProps} />;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (newPassword !== confirmNewPassword) {
      setError("The new password and confirmation password do not match.");
      return;
    }

    if (newPassword.length < 6) {
      setError("Password must contain at least 6 characters.");
      return;
    }

    if (hasSetPassword && !oldPassword) {
      setError("Please enter your current password.");
      return;
    }

    const user = auth.currentUser;

    if (!user) {
      setError(
        "Unable to verify the authenticated coach account. Please log in again."
      );
      return;
    }

    setLoading(true);

    try {
      // Re-authenticate if coach already has a password set
      if (hasSetPassword && user.email) {
        const credential = EmailAuthProvider.credential(user.email, oldPassword);
        await reauthenticateWithCredential(user, credential);
      }

      await updatePassword(user, newPassword);

      const storedUser = localStorage.getItem("coachUser");

      if (storedUser) {
        const parsed = JSON.parse(storedUser);
        const userId = parsed.id || parsed.uid;

        if (userId) {
          await updateDoc(doc(db, "users", userId), {
            onboardStatus: "in_process",
            passwordUpdated: true,
            passwordUpdatedAt: new Date(),
          });
        }

        parsed.onboardStatus = "in_process";
        parsed.passwordUpdated = true;

        localStorage.setItem("coachUser", JSON.stringify(parsed));
        localStorage.setItem("currentUser", JSON.stringify(parsed));
      }

      setCompletedSteps({
        1: true,
      });

      setCurrentStep(2);
      showSuccessToast();
    } catch (err) {
      console.error("Password update error:", err);

      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setError("Current password is incorrect. Please try again.");
      } else {
        setError(
          err.message ||
          "Unable to update your password at this time. Please log in again using your invitation link and try again."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const completedStepCount = Object.keys(completedSteps).filter(
    (key) => completedSteps[key]
  ).length;

  const progressPercentage =
    ((currentStep - 1) / (onboardingSteps.length - 1)) * 100;

  const showStepContent = currentStep > 1;

  const currentStepInfo = onboardingSteps.find(
    (step) => step.id === currentStep
  );


  return (
    <div className="relative min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Toast */}
      {toast.show && (
        <div className="fixed top-6 right-6 z-50 animate-[slideIn_0.3s_ease-out]">
          <div className="w-[360px] bg-white border border-green-200 shadow-xl rounded-2xl p-4 flex items-start gap-3">
            <div className="w-11 h-11 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>

            <div className="flex-1">
              <h4 className="text-sm font-bold text-gray-900">
                {toast.title}
              </h4>
              <p className="text-sm text-gray-600 mt-1">{toast.message}</p>
            </div>

            <button
              type="button"
              onClick={() =>
                setToast({
                  show: false,
                  type: "",
                  title: "",
                  message: "",
                })
              }
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 mb-5">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0">
                <ShieldCheck className="h-6 w-6 text-white" />
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                  Coach Onboarding
                </h2>

                <p className="text-sm text-gray-500">
                  Complete each step to activate your coach account.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="text-right">
                <p className="text-xs text-gray-500">Current Step</p>
                <p className="text-sm font-semibold text-gray-900">
                  {currentStepInfo?.title}
                </p>
              </div>

              <div className="h-10 w-10 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center">
                <span className="text-sm font-bold text-blue-700">
                  {currentStep}/{onboardingSteps.length}
                </span>
              </div>
            </div>
          </div>

          {/* Overall Progress Bar */}
          <div className="mt-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-gray-600">
                Overall Progress
              </p>

              <p className="text-xs font-semibold text-blue-700">
                {Math.round(progressPercentage)}%
              </p>
            </div>

            <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
              <div
                className="h-2 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full transition-all duration-500"
                style={{
                  width: `${progressPercentage}%`,
                }}
              />
            </div>

            <p className="text-xs text-gray-500 mt-2">
              {completedStepCount} of {onboardingSteps.length} steps completed
            </p>
          </div>
        </div>

        {/* Compact Stepper */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-5">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {onboardingSteps.map((step) => {
              const Icon = step.icon;
              const isCompleted = step.id < currentStep;
              const isActive = step.id === currentStep;

              return (
                <div
                  key={step.id}
                  className={`flex items-center gap-3 rounded-xl border px-3 py-3 transition-all ${isCompleted
                      ? "bg-green-50 border-green-200"
                      : isActive
                        ? "bg-blue-50 border-blue-200 shadow-sm"
                        : "bg-gray-50 border-gray-200"
                    }`}
                >
                  <div
                    className={`h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0 ${isCompleted
                        ? "bg-green-500 text-white"
                        : isActive
                          ? "bg-blue-600 text-white"
                          : "bg-white text-gray-400 border border-gray-200"
                      }`}
                  >
                    {isCompleted ? (
                      <CheckCircle className="h-5 w-5" />
                    ) : (
                      <Icon className="h-4 w-4" />
                    )}
                  </div>

                  <div className="min-w-0">
                    <p
                      className={`text-xs font-semibold truncate ${isCompleted
                          ? "text-green-700"
                          : isActive
                            ? "text-blue-700"
                            : "text-gray-500"
                        }`}
                    >
                      {step.title}
                    </p>

                    <p className="text-[11px] text-gray-400">
                      {isCompleted
                        ? "Completed"
                        : isActive
                          ? "In Progress"
                          : "Pending"}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-5xl mx-auto bg-white rounded-2xl shadow-sm p-5 sm:p-6 border border-gray-100">
          {showStepContent ? (
            <div>{renderStepContent()}</div>
          ) : (
            <div>
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="h-10 w-10 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center">
                    <Lock className="h-5 w-5 text-blue-600" />
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      Create Account Password
                    </h3>

                    <p className="text-sm text-gray-600">
                      Create a secure password for your coach account.
                    </p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                {hasSetPassword && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Current Password
                    </label>

                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Lock className="h-5 w-5 text-gray-400" />
                      </div>

                      <input
                        type={showOld ? "text" : "password"}
                        required={hasSetPassword}
                        value={oldPassword}
                        onChange={(e) => setOldPassword(e.target.value)}
                        placeholder="Enter your current password"
                        className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />

                      <button
                        type="button"
                        onClick={() => setShowOld(!showOld)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                      >
                        {showOld ? (
                          <EyeOff className="h-5 w-5" />
                        ) : (
                          <Eye className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Password
                  </label>

                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>

                    <input
                      type={showNew ? "text" : "password"}
                      required
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter your new password"
                      className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />

                    <button
                      type="button"
                      onClick={() => setShowNew(!showNew)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    >
                      {showNew ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm New Password
                  </label>

                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>

                    <input
                      type={showConfirm ? "text" : "password"}
                      required
                      value={confirmNewPassword}
                      onChange={(e) =>
                        setConfirmNewPassword(e.target.value)
                      }
                      placeholder="Confirm your new password"
                      className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />

                    <button
                      type="button"
                      onClick={() => setShowConfirm(!showConfirm)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    >
                      {showConfirm ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="p-4 bg-blue-50 text-blue-800 rounded-xl text-sm border border-blue-200">
                  <p className="font-semibold">Password Requirements</p>

                  <ul className="list-disc list-inside mt-1 space-y-1">
                    <li>Password must contain at least 6 characters.</li>
                    <li>
                      Use a combination of uppercase letters, lowercase letters,
                      numbers, or symbols for stronger security.
                    </li>
                  </ul>
                </div>

                <div className="p-4 bg-amber-50 text-amber-800 rounded-xl text-sm border border-amber-200">
                  <p className="font-semibold">⚠ Important</p>

                  <p className="mt-1">
                    After updating your password, the invitation link will no
                    longer work for logging in. Please use your updated
                    password, not the one from the link, for all future logins.
                  </p>
                </div>

                {error && (
                  <div className="text-red-600 font-semibold p-3 bg-red-50 border border-red-200 rounded-lg">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="group relative w-full flex justify-center py-3 px-4 text-sm font-medium rounded-xl text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  {loading ? "Updating password..." : "Save Password and Continue"}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}